import {
  type FactsPlaceOfServicesListResponse,
  type FactsSpecialtyGroupsListResponse,
  type ReferralSteeragePersonElationReferralTemplateRetrieveResponse,
  type ProvidersUserPeopleRetrieveResponse,
  type ReferralSteerageProvidersCreateBodyParams,
  type ReferralSteerageProvidersListResponse,
  type ReferralSteerageRetrieveResponse,
} from '@fireflyhealth/api'

/**
 * Fails to compile unless `T` is `never`. Used below to assert a runtime list covers every value
 * of the schema union it is bound to.
 */
type AssertNever<T extends never> = T

export enum SteerageProviderStatus {
  UnderReview = 'under_review',
  Accepted = 'accepted',
  Rejected = 'rejected',
}

/**
 * Provider attached to a Steerage. Sourced directly from the generated client
 * (`referralSteerageProviders.list`) so the shape tracks the backend serializer
 * in snake_case.
 */
export type SteerageProvider = ReferralSteerageProvidersListResponse[number]

export interface CareKey {
  pk: number
  person: number | null
  createdAt: string | null
  isEligible?: boolean
}

/**
 * The steerage detail payload comes straight from the generated client
 * (`referralSteerage.retrieve`), so the shape tracks the backend serializer in
 * snake_case exactly. (The originating case is threaded as the `activeCaseId` prop
 * through SteerageView → ReferralDetails rather than smuggled onto this object.)
 */
export type Steerage = ReferralSteerageRetrieveResponse

// Sourced from the generated /referral/steerage/{person}/elation-referral-template/ response.
export type ElationReferralTemplate = ReferralSteeragePersonElationReferralTemplateRetrieveResponse

export enum SteerageProviderDataSource {
  Ribbon = 'ribbon',
  Talon = 'talon',
  Manual = 'manual',
}

/**
 * The recommendation statuses, straight off the generated schema — the endpoint declares these as
 * an enum, so there is nothing to restate here.
 *
 * `asRecommendationStatus` narrows arbitrary runtime values against this list, so the list has to
 * be complete, and it is tied to the schema in both directions: `satisfies` rejects a value the
 * backend no longer serves, and the assertion below fails to compile if the backend adds one this
 * list has not caught up with.
 *
 * A const object rather than a TypeScript `enum`: comparing the response's union against an enum
 * member is unsound — the compiler accepts it while the two types share nothing. As plain strings
 * the comparisons are exactly what they read as.
 */
export type RecommendationStatus = NonNullable<SteerageProvider['recommendation_status']>

export const RECOMMENDATION_STATUS = {
  NO_RATING: 'NO_RATING',
  RECOMMENDED: 'RECOMMENDED',
  NOT_RECOMMENDED: 'NOT_RECOMMENDED',
  NEUTRAL: 'NEUTRAL',
} as const satisfies Record<string, RecommendationStatus>

type _EveryRecommendationStatusListed = AssertNever<
  Exclude<RecommendationStatus, (typeof RECOMMENDATION_STATUS)[keyof typeof RECOMMENDATION_STATUS]>
>

/** Narrows a response's status string, so an unrecognised one reads as no rating at all. */
export const asRecommendationStatus = (value: unknown): RecommendationStatus | null =>
  Object.values(RECOMMENDATION_STATUS).find(status => status === value) ?? null

/**
 * @knipskip - the only read is a dynamic index, `SteerageProviderWaiverLevel[provider.waiver_level]`
 * in SteerageProviderListItem, so no member is referenced by name for knip to find.
 */
export enum SteerageProviderWaiverLevel {
  Provider = 'Specific Provider',
  Facility = 'Entire Practice',
  'Not Applicable' = 'Not Eligible',
}

export interface SpecialtyDropdownOption {
  id: number
  label: string
  source: 'SpecialtyGroup' | 'LocationType'
  services?: Service[] | null
  clinical_focus_areas?: ClinicalFocusArea[] | null
  is_deprecated?: boolean | null
  does_virtual_partner_exist: boolean
  place_of_service?: PlaceOfService[] | null
}

// Sourced from the generated /facts/specialty-groups/ response (the same
// `referral_SlimService` / clinical-focus-area shapes the /facts/location-types/
// endpoint reuses), Picking the fields the UI consumes so the types — and their
// nullability — track the backend serializers without hand-maintained duplicates.
type SpecialtyGroupFact = FactsSpecialtyGroupsListResponse[number]
type SpecialtyGroupFactService = SpecialtyGroupFact['services'][number]
type SpecialtyGroupFactClinicalFocusArea = SpecialtyGroupFact['clinical_focus_areas'][number]

export type Service = Pick<SpecialtyGroupFactService, 'id' | 'description'> & {
  category: Pick<SpecialtyGroupFactService['category'], 'id' | 'description' | 'ordering'>
}

export type ClinicalFocusArea = Pick<SpecialtyGroupFactClinicalFocusArea, 'id' | 'label'>

export const ReferralPriority = {
  STANDARD: 'Standard',
  MEDIUM: 'Soonest Available',
  URGENT: 'Urgent',
}

/** @public - reserved for future use; keep export to satisfy Knip */
export const SteerageSegment = {
  HIGH_TCOC: 'High TCoC',
  LOW_TCOC: 'Low TCoC',
  COMMODITY: 'Commodity',
}

export enum CuratedProviderType {
  VIRTUAL = 'Virtual',
  AT_HOME_TEST_KIT = 'At Home Test Kit',
  IN_HOME = 'In Home',
  IN_PERSON = 'In Person',
}

/**
 * Derived from the generated API schema — stays in sync with the backend automatically.
 * Equals `network_partner_agreement_type` from `ReferralSteerageProvidersCreateBodyParams`.
 */
export type AgreementTypeConfig = NonNullable<
  ReferralSteerageProvidersCreateBodyParams['network_partner_agreement_type']
>

/**
 * Runtime constants for agreement type comparisons.
 * Typed as `Record<string, AgreementTypeConfig>` so TypeScript errors if a value
 * is not accepted by the generated API schema.
 *
 * The same-name merging below (type + const) is valid TypeScript — types and values
 * live in separate namespaces, exactly like compiled enums. The base `no-redeclare`
 * ESLint rule does not understand this split, so it is suppressed here.
 */
export const AgreementTypeConfig: Record<string, AgreementTypeConfig> = {
  CLIENT_CONTRACT: 'client_contract',
  DIRECT_CONTRACT: 'direct_contract',
  PREFERRED_VENDOR: 'preferred_vendor',
}

/**
 * Derived from the generated API schema — stays in sync with the backend automatically.
 * Equals `reason_for_exception` from `ReferralSteerageProvidersCreateBodyParams`.
 */
export type ReasonForException = NonNullable<
  ReferralSteerageProvidersCreateBodyParams['reason_for_exception']
>

/**
 * The authorization statuses, straight off the generated schema — the endpoint declares these as
 * an enum, so there is nothing to restate here.
 *
 * The dropdown needs them at runtime as well as in the type system, and a hand-kept list is
 * exactly what drifts, so the list is tied to the schema in both directions: `satisfies` rejects a
 * value the backend no longer serves, and the assertion below fails to compile if the backend
 * adds one this list has not caught up with.
 */
export type ReferralInsuranceAuthorizationStatus = NonNullable<
  NonNullable<NonNullable<Steerage['referral']>['referral_insurance_authorization']>['status']
>

export const REFERRAL_INSURANCE_AUTHORIZATION_STATUSES = [
  'Pending Approval',
  'Pending Clinical Info',
  'Pending OON Approval',
  'Provider/Date Change',
  'Denied',
  'Accepted',
] as const satisfies readonly ReferralInsuranceAuthorizationStatus[]

type _EveryAuthorizationStatusListed = AssertNever<
  Exclude<
    ReferralInsuranceAuthorizationStatus,
    (typeof REFERRAL_INSURANCE_AUTHORIZATION_STATUSES)[number]
  >
>

/** The one status the form treats specially: it makes the authorization number required. */
export const AUTHORIZATION_STATUS_ACCEPTED: ReferralInsuranceAuthorizationStatus = 'Accepted'

/** Narrows the form's selected status, so anything else is saved as no status at all. */
export const asReferralInsuranceAuthorizationStatus = (
  value: unknown,
): ReferralInsuranceAuthorizationStatus | null =>
  REFERRAL_INSURANCE_AUTHORIZATION_STATUSES.find(status => status === value) ?? null

/**
 * The insurance plan types, straight off the generated person schema. Lowercase, as the backend
 * stores them — the plan type is uppercased where it is displayed, not where it is compared.
 *
 * Only PPO is named. The steerage UI asks whether a plan is PPO or anything else, so the other
 * five types are only ever the second branch and a completeness assertion would be wrong here;
 * `satisfies` still ties the one name to the schema, so renaming or dropping it fails to compile.
 */
type ProductType = NonNullable<
  NonNullable<ProvidersUserPeopleRetrieveResponse['insurance_info']>['product_type']
>

export const PRODUCT_TYPE = {
  PPO: 'ppo',
} as const satisfies Record<string, ProductType>

/**
 * The member service levels, straight off the generated schema.
 *
 * Every level drives a branch in the steerage UI, so this list has to be complete and is tied to
 * the schema in both directions the same way `RECOMMENDATION_STATUS` is.
 */
type MemberServiceLevel = NonNullable<Steerage['member_service_level']>

export const MEMBER_SERVICE_LEVEL = {
  CARE: 'Care',
  COVERAGE: 'Coverage',
  CARE_AND_COVERAGE: 'Care and Coverage',
} as const satisfies Record<string, MemberServiceLevel>

type _EveryMemberServiceLevelListed = AssertNever<
  Exclude<MemberServiceLevel, (typeof MEMBER_SERVICE_LEVEL)[keyof typeof MEMBER_SERVICE_LEVEL]>
>

export type PlaceOfService = FactsPlaceOfServicesListResponse[number]
